import { trackUser, getMappedEnterpriseList, getVersion } from 'spark-api/utilityService';
import { getEnterpriseCtmsRefreshDate } from 'spark-api/enterpriseService';
import {
    INITIALIZE_AUTH,
    AUTH_FAILURE,
    LOGOUT_USER,
    authSuccess,
    appReady,
    kcToken,
    setMappedEnterpriseData,
    getMappedEnterpriseData
} from 'spark-state/common/app/reducer';
import {
    CHANGE_ENTERPRISE,
    setCurrentEnterprise,
    setCurrentEnterpriseRole,
    setCurrentUserInfo,
    loadingUserSettings,
    setUserInfo,
    setDomainValue,
    setReportEnabledForSponsor,
    setReportEnabledForCRO
} from 'spark-state/common/user/userReducer';
import { setCtmsRefreshDate, setVersionNumber } from 'spark-state/enterprise/enterpriseReducer';
import Roles from 'spark-types/Roles';

// Helper to find role matching URL category (for email deep links)
const findRoleByCategory = (rolesData, urlCategory) => {
    if (!rolesData || !urlCategory) return null;
    
    // Match category case-insensitively
    const targetCategory = urlCategory.toUpperCase();
    const matchingRole = rolesData.find(role => 
        role.category && role.category.toUpperCase() === targetCategory
    );
    
    return matchingRole ? matchingRole.roleVal : null;
};

const authenticationMiddleware = (auth, history) => ({ dispatch, getState }) => next => action => {
    const nextState = next(action);
    switch (action.type) {
        case CHANGE_ENTERPRISE:
        case INITIALIZE_AUTH: {
            //What do we do if initialization errors at any point?
            return initialize(auth, dispatch, action.enterpriseId, history, getState);
        }
        case AUTH_FAILURE: {
            auth.redirectToLogin();
            break;
        }
        case LOGOUT_USER: {
            auth.logout();
            break;
        }
        default: {
            return nextState;
        }
    }
};

const initialize = async (auth, dispatch, entId, history, getState) => {
    let rolesData = [];
    let slId = "";
    let emailEntId = "";

if (!entId) {
  if(history.location.search){
    let str = history.location.search;
    if(str.search('&') !== -1){ // when &1d2= is encountered
        let splitStr = str.split('&');
        let slIdStr = splitStr[0];
        let entIdStr = splitStr[1];
        slId = slIdStr.slice(4);
        entId =  entIdStr.slice(4);
   }else if(str.search('id3') !== -1){  // ?id3=1
     let splitStr = str.split('id3=');
     emailEntId = splitStr[1];
     entId = emailEntId;
   }
   else{ // without id2
      slId = str.slice(4);
      entId = 1;
  }
}
        await auth.initialize();
        dispatch(authSuccess());
        dispatch(kcToken(auth.getToken()));
        dispatch(loadingUserSettings());
       let [userInfo] = await Promise.all([auth.loadUserInfo().catch(error => {})]);     

        if (userInfo && !userInfo.isEnterpriseAdmin) {
            await dispatch(getMappedEnterpriseList()).then(response => {
                // SAF-20237: Role Assignment - Switch Roles and its Precedence - PSR-4763
                // Set domain values based on enterprise selection
                if(slId){
                   let filteredArr = response.data.filter(value => value.entId === Number(entId));
                   dispatch(setDomainValue(filteredArr && filteredArr.length && filteredArr[0].domain, userInfo, filteredArr && filteredArr.length && filteredArr[0].isEntAutoEnabled));
                }else if(emailEntId){
                    let filteredArr = response.data.filter(value => value.entId === Number(entId));
                    dispatch(setDomainValue(filteredArr && filteredArr.length && filteredArr[0].domain, userInfo, filteredArr && filteredArr.length && filteredArr[0].isEntAutoEnabled));
                }else{
                    entId = response && response.data && response.data[0].entId;
                    dispatch(setDomainValue(response && response.data && response.data[0].domain, userInfo, response && response.data && response.data[0].isEntAutoEnabled));
                }
                
                // Dispatch to Redux - reducer handles rolesByModule transformation
                dispatch(setMappedEnterpriseData(response));
                
                // SAF-20237: Read transformed data from Redux state (single source of truth)
                // Avoids duplicate transformation logic - reducer already converted rolesByModule to flat roles array
                const state = getState();
                const mappedEnterpriseData = getMappedEnterpriseData(state);
                const currentEnterprise = mappedEnterpriseData && mappedEnterpriseData.find(
                    ent => ent.entId === Number(entId)
                );
                
                // Extract roles from Redux state (already transformed with category fields)
                rolesData = currentEnterprise ? currentEnterprise.roles : [];
            });
        } else dispatch(setUserInfo(userInfo, Roles.ENTERPRISE_ADMIN));

    }
    
    //TODO: This is where we need to load the real enterprise ID from the user's email
    //if entId wasn't provided as an argument

    dispatch(setCurrentEnterprise(entId));
    //Roles and user info determine what routes we show
    await loadEnterpriseUser(auth, dispatch, entId, rolesData, history);
    dispatch(trackUser(entId)); // passing entId parameter to trackuser API as pat of 6.4 release

    dispatch(appReady()); //Yay! Show the app now
};

const loadEnterpriseUser = async (auth, dispatch, entId, rolesData, history) => {
    //SAF-17154 - added IB_Admin in userROles
    // SAF-20491: Added IB_SUPPORT and GAP_PACKET_SUPPORT (split from single Safety Support role)
    let userRoles = [Roles.SAFETY_ADMIN, Roles.SAFETY_MANAGER, Roles.SAFETY_MONITOR, Roles.COUNTRY_LEAD, Roles.SAFETY_SUPPORT, Roles.GAP_PACKET_ADMIN, Roles.IB_ADMIN, Roles.IB_COUNTRY_LEAD, Roles.IB_MONITOR, Roles.IB_SUPPORT, Roles.GAP_PACKET_SUPPORT];
     
    let [userInfo] = await Promise.all([auth.loadUserInfo().catch(error => {})]);
    
    // Check for category parameter in URL (email deep links)
    let categoryFromUrl = null;
    if (history && history.location && history.location.search) {
        const params = new URLSearchParams(history.location.search);
        categoryFromUrl = params.get('category');
    }

    // If category exists (email deep link), find matching role by category
    let newRole = '';
    if (categoryFromUrl) {
        newRole = findRoleByCategory(rolesData, categoryFromUrl) || '';
    }

    // If no category match or no category, use API priority (first role)
    if (!newRole) {
        let rolesList = rolesData && rolesData.map(role => role.roleVal);
        newRole = (rolesList && rolesList.length > 0) ? rolesList[0] : '';
    }
    
    // SAF-20237: Extract and store reportEnabledForSponsor flag for the selected role
    // This flag controls report access for recipient roles only
    const selectedRoleData = rolesData && rolesData.find(r => r.roleVal === newRole);
    const reportEnabled = selectedRoleData && selectedRoleData.reportEnabledForSponsor ? true : false;
    const reportEnabledCRO = selectedRoleData && selectedRoleData.reportEnabledForCRO ? true : false;
    
    let currentEntRole = userRoles.includes(newRole) ? 'MNGT' : 'RECP';
    dispatch(setCurrentEnterpriseRole(currentEntRole));
    dispatch(setReportEnabledForSponsor(reportEnabled));
    dispatch(setReportEnabledForCRO(reportEnabledCRO));
    dispatch(setUserInfo(userInfo, newRole));
    // Added this capture the user info on page load
    dispatch(setCurrentUserInfo(userInfo));
    dispatch(getVersion()).then(response => dispatch(setVersionNumber(response)));

   
    if (userRoles.includes(newRole) && entId) {
        dispatch(getEnterpriseCtmsRefreshDate(entId)).then(response =>
            dispatch(setCtmsRefreshDate(response))
           
        );
    }
};

export default authenticationMiddleware;
